import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Grid, Theme, Typography, makeStyles } from '@material-ui/core';
import { getEnrollmentStatusThunk } from '@spike/payments-action';
import { EnrollmentStatus } from '@spike/payments-model';
import {
    deactivateSubscriptionThunk,
    fetchSubscriptionsThunk,
    purgeSubscriptionsThunk
} from 'actions/subscriptions/SubscriptionsActions';
import { Button, ConfirmDialog, OverFullWindow, Spinner } from 'components/UI';
import { useApiClientWrapper, usePremiumPlan } from 'hooks';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { BusinessSettingsTitle } from '../UI';
import { PaymentsFlyer } from '../UI/PaymentsFlyer';
import { FreePlanCard } from './Cards/FreePlanCard';
import { PremiumPlanCard } from './Cards/PremiumPlanCard';
import { DowngradePlanForm } from './Downgrade/DowngradePlanForm';
import { DowngradeSuccess } from './Downgrade/DowngradeSuccess';
import { UpgradePlanForm } from './Upgrade/UpgradePlanForm';
import { UpgradeSuccess } from './Upgrade/UpgradeSuccess';

export interface Props {
    openSidebar?: () => void;
    isMobile?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        width: '100%'
    },
    modal: {
        zIndex: 1299
    },
    modalContainer: {
        maxWidth: 465,
        display: 'flex',
        flex: '0 0 100%',
        flexDirection: 'column',
        padding: '40px 0px 20px',
        marginLeft: 'auto',
        marginRight: 'auto',

        [theme.breakpoints.up('md')]: {
            flex: '0 0 465px',
            padding: '90px 0px',
            justifyContent: 'center'
        }
    },
    dialog: {
        '& .MuiPaper-root': {
            maxWidth: 464,
            minWidth: 'unset'
        },
        '& .MuiButtonBase-root': {
            'height': 47,
            'width': '100%',
            'borderRadius': 30,

            '& span': {
                fontSize: 16,
                fontWeight: 600
            }
        }
    }
}));

const statuses = {
    Initial: 'initial',

    Upgrade: 'upgrade',
    UpgradeSuccess: 'upgrade-success',

    Downgrade: 'downgrade',
    DowngradeSuccess: 'downgrade-success'
} as const;

type Status = (typeof statuses)[keyof typeof statuses];

export const Plans: React.FunctionComponent<Props> = ({ openSidebar, isMobile }) => {
    const classes = useStyles();

    const dispatch = useDispatch();
    const activeSubscription = usePremiumPlan();
    const apiClientWrapper = useApiClientWrapper();

    const [status, setStatus] = useState<Status>(statuses.Initial);

    const [showConfirmDowngrade, setShowConfirmDowngrade] = useState(false);

    const loading = useSelector<RootState, boolean>(state => state.subscriptions.loading);

    const entrollmentStatus = useSelector<RootState, EnrollmentStatus | undefined>(
        state => state.payments.enrollmentStatus
    );

    /**
     * Fetch subscriptions
     */
    const fetch = async () => dispatch(fetchSubscriptionsThunk());

    /**
     * Upgrade plan handler
     */
    const upgradeHandler = () => {
        setStatus(statuses.UpgradeSuccess);
    };

    /**
     * Show upgrade handler
     */
    const showUpgradeHandler = () => {
        dispatch(purgeSubscriptionsThunk());
        setStatus(statuses.Upgrade);
    };

    /**
     * Downgrade plan handler
     */
    const downgradeHandler = async () => {
        if (!activeSubscription?.id) {
            return;
        }

        await dispatch(deactivateSubscriptionThunk(activeSubscription?.id));
        setShowConfirmDowngrade(false);
        setStatus(statuses.DowngradeSuccess);
        fetch();
    };

    const closeUpgradeHandler = () => {
        setStatus(statuses.Initial);
    };

    useEffect(() => {
        if (status === statuses.Initial) {
            fetch();
            dispatch(getEnrollmentStatusThunk(apiClientWrapper));
        }
        // eslint-disable-next-line
    }, [status]);

    return (
        <Box className={classes.root}>
            <BusinessSettingsTitle>
                {isMobile && (
                    <FontAwesomeIcon
                        onClick={openSidebar}
                        icon={faArrowLeft}
                        size="2x"
                    />
                )}
                Plans
            </BusinessSettingsTitle>

            {loading && <Spinner />}

            {!loading && (
                <>
                    <Grid
                        container
                        spacing={4}
                    >
                        <Grid
                            item
                            xs={12}
                        >
                            {activeSubscription ? (
                                <>
                                    <PremiumPlanCard
                                        subscription={activeSubscription}
                                        onDowngrade={() => setStatus(statuses.Downgrade)}
                                    />
                                </>
                            ) : (
                                <FreePlanCard onUpgrade={showUpgradeHandler} />
                            )}
                        </Grid>
                        <Grid
                            item
                            xs={12}
                        >
                            {entrollmentStatus && !entrollmentStatus?.enabled && <PaymentsFlyer />}
                        </Grid>
                    </Grid>
                </>
            )}

            {(status === statuses.Upgrade || status === statuses.UpgradeSuccess) && (
                <OverFullWindow
                    title="Change your Plan"
                    className={classes.modal}
                    onClose={closeUpgradeHandler}
                >
                    {status === statuses.UpgradeSuccess && <UpgradeSuccess onClose={closeUpgradeHandler} />}

                    {status === statuses.Upgrade && (
                        <Box className={classes.modalContainer}>
                            <UpgradePlanForm
                                loading={loading}
                                onUpgrade={upgradeHandler}
                            />
                        </Box>
                    )}
                </OverFullWindow>
            )}

            {(status === statuses.Downgrade || status === statuses.DowngradeSuccess) && (
                <OverFullWindow
                    title="Change your Plan"
                    className={classes.modal}
                    onClose={() => setStatus(statuses.Initial)}
                >
                    <Box className={classes.modalContainer}>
                        {status === statuses.DowngradeSuccess && (
                            <DowngradeSuccess onClose={() => setStatus(statuses.Initial)} />
                        )}

                        {status === statuses.Downgrade && activeSubscription && (
                            <DowngradePlanForm
                                loading={loading}
                                subscription={activeSubscription}
                                onDowngrade={() => setShowConfirmDowngrade(true)}
                            />
                        )}
                    </Box>
                </OverFullWindow>
            )}

            <ConfirmDialog
                isSuccess
                title={<Typography variant="h6">Confirm Downgrade</Typography>}
                question={
                    <>
                        Are you sure you want to downgrade to the <b>Free Plan</b>?
                    </>
                }
                actions={
                    <>
                        <Button
                            label="No"
                            color="black"
                            disabled={loading}
                            onClick={() => setShowConfirmDowngrade(false)}
                        />
                        <Button
                            label="Yes"
                            color="green"
                            variant="primary"
                            loading={loading}
                            onClick={downgradeHandler}
                        />
                    </>
                }
                className={classes.dialog}
                open={showConfirmDowngrade}
                onCancel={() => setShowConfirmDowngrade(false)}
            />
        </Box>
    );
};
